import * as THREE from 'three';
import {AbstractDirectionalGuidePointSnapStrategy} from './AbstractDirectionalGuidePointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest} from './PointSnapTypes';
import {isSketchCircleObject} from '../../mesh/guards/isSketchCircleObject';
import {isSketchArcObject} from '../../mesh/guards/isSketchArcObject';
import {isSketchEllipseObject} from '../../mesh/guards/isSketchEllipseObject';
import {isSketchLineObject} from '../../mesh/guards/isSketchLineObject';
import type {SketchLineObject} from '../../mesh/sketch/SketchLineObject';
import type {SketchCircleObject} from '../../mesh/sketch/SketchCircleObject';
import type {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import type {SketchEllipseObject} from '../../mesh/sketch/SketchEllipseObject';
import {POINT_SNAP_GUIDE_DARK_ORANGE_COLOR} from '../../material';
import {
    computeLinePerpendicularFootLocal,
    computeCirclePerpendicularFootsLocal,
    computeArcPerpendicularFootsLocal,
    computeEllipsePerpendicularFootsLocal,
} from '../perpendicular/PerpendicularLineComputer';



/**
 * 垂直捕捉支持的源几何类型。
 */
type PerpendicularGuideTarget = SketchLineObject | SketchCircleObject | SketchArcObject | SketchEllipseObject;

/**
 * 判断对象是否支持垂直捕捉。
 *
 * @param object 待判断对象。
 * @returns 支持垂直捕捉时返回 true。
 */
function isPerpendicularGuideTarget(object: THREE.Object3D): object is PerpendicularGuideTarget {
    return isSketchLineObject(object)
        || isSketchCircleObject(object)
        || isSketchArcObject(object)
        || isSketchEllipseObject(object);
}

/**
 * 判断垂直捕捉源对象是否为绘制预览对象。
 *
 * @param target 垂直捕捉源对象。
 * @returns 源对象是绘制预览对象时返回 true。
 */
function isPerpendicularPreviewTarget(target: PerpendicularGuideTarget): boolean {
    if (isSketchLineObject(target)) {
        return target.isDrawingPreviewLine;
    }
    if (isSketchArcObject(target)) {
        return target.isDrawingPreviewArc;
    }
    return false;
}

/**
 * 垂直捕捉策略。
 *
 * 设计说明：
 * 1. 公共的引导线生命周期、材质和吸附距离判定由方向引导捕捉父类管理；
 * 2. 本类只负责从统一 hover 链路解析可垂直的源几何，并计算从绘制起点到源几何的垂足方向；
 * 3. 源几何来自 `PointSnapRequest.hoveredObject`，避免内部二次扫边造成与 Hover 高亮不一致。
 */
export class PerpendicularPointSnapStrategy extends AbstractDirectionalGuidePointSnapStrategy<PerpendicularGuideTarget> {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Perpendicular;

    /** 垂直捕捉屏幕像素阈值。 */
    protected readonly guideScreenDistanceThresholdPx = 5;

    /** 垂直捕捉结果权重。 */
    protected readonly guideSnapWeight = 900;

    /** 垂线预览普通状态颜色。 */
    protected readonly guidePreviewLineColor: THREE.ColorRepresentation = POINT_SNAP_GUIDE_DARK_ORANGE_COLOR;

    /** 垂线预览高亮状态颜色。 */
    protected readonly guideHighlightLineColor: THREE.ColorRepresentation = POINT_SNAP_GUIDE_DARK_ORANGE_COLOR;

    /** 垂线预览屏幕空间虚线段长度。 */
    protected readonly guideScreenSpaceDashSizePx = 4;

    /** 垂线预览屏幕空间虚线间隔长度。 */
    protected readonly guideScreenSpaceGapSizePx = 3;

    /** 普通状态材质名称。 */
    protected readonly guidePreviewMaterialName = '垂线预览-屏幕空间虚线材质';

    /** 高亮状态材质名称。 */
    protected readonly guideHighlightMaterialName = '垂线高亮-屏幕空间虚线材质';

    /**
     * 解析当前可作为垂直参考的源几何。
     *
     * @param request 点捕捉请求。
     * @returns 当前活跃草图中的垂直参考源；无合法源对象时返回 null。
     */
    protected resolveGuideTarget(request: PointSnapRequest): PerpendicularGuideTarget | null {
        return this.resolveHoveredActiveSketchEdge(
            request,
            isPerpendicularGuideTarget,
            isPerpendicularPreviewTarget,
        );
    }

    /**
     * 根据源几何生成垂线引导方向。
     *
     * @param firstPoint 绘制起点。
     * @param target 垂直参考源对象。
     * @returns 一条或多条垂线方向。
     */
    protected createGuideDirections(firstPoint: THREE.Vector3, target: PerpendicularGuideTarget): THREE.Vector3[] {
        const footPointsWorld = this.computeFootPointsWorld(firstPoint, target);
        return footPointsWorld.map((footWorld) => new THREE.Vector3().subVectors(footWorld, firstPoint));
    }

    /**
     * 计算绘制起点到源几何的垂足。
     *
     * @param firstPoint 绘制起点。
     * @param target 垂直参考源对象。
     * @returns 垂足世界坐标列表。
     */
    private computeFootPointsWorld(firstPoint: THREE.Vector3, target: PerpendicularGuideTarget): THREE.Vector3[] {
        if (isSketchLineObject(target)) {
            return this.computeLineFootPointsWorld(firstPoint, target);
        }
        if (isSketchCircleObject(target)) {
            return this.computeCircleFootPointsWorld(firstPoint, target);
        }
        if (isSketchArcObject(target)) {
            return this.computeArcFootPointsWorld(firstPoint, target);
        }
        if (isSketchEllipseObject(target)) {
            return this.computeEllipseFootPointsWorld(firstPoint, target);
        }
        return [];
    }

    /**
     * 计算直线垂足。
     *
     * @param firstPoint 绘制起点。
     * @param target 直线源对象。
     * @returns 垂足世界坐标列表。
     */
    private computeLineFootPointsWorld(firstPoint: THREE.Vector3, target: SketchLineObject): THREE.Vector3[] {
        const referencePlane = target.referencePlane;
        const fromLocal = referencePlane.worldToLocal(firstPoint);
        const startLocal = referencePlane.worldToLocal(target.startPoint.getPosition());
        const endLocal = referencePlane.worldToLocal(target.endPoint.getPosition());
        const footLocal = computeLinePerpendicularFootLocal(fromLocal, startLocal, endLocal);
        if (!footLocal) {
            return [];
        }
        return [referencePlane.localToWorld(footLocal.x, footLocal.y)];
    }

    /**
     * 计算圆垂足。
     *
     * @param firstPoint 绘制起点。
     * @param target 圆源对象。
     * @returns 垂足世界坐标列表。
     */
    private computeCircleFootPointsWorld(firstPoint: THREE.Vector3, target: SketchCircleObject): THREE.Vector3[] {
        const referencePlane = target.referencePlane;
        const fromLocal = referencePlane.worldToLocal(firstPoint);
        const centerLocal = referencePlane.worldToLocal(target.getCenterWorld());
        const footsLocal = computeCirclePerpendicularFootsLocal(fromLocal, centerLocal, target.getRadius());
        return footsLocal.map((footLocal) => referencePlane.localToWorld(footLocal.x, footLocal.y));
    }

    /**
     * 计算圆弧垂足。
     *
     * @param firstPoint 绘制起点。
     * @param target 圆弧源对象。
     * @returns 垂足世界坐标列表。
     */
    private computeArcFootPointsWorld(firstPoint: THREE.Vector3, target: SketchArcObject): THREE.Vector3[] {
        const referencePlane = target.referencePlane;
        const fromLocal = referencePlane.worldToLocal(firstPoint);
        const definition = target.getArcDefinition();
        const centerLocal = referencePlane.worldToLocal(definition.centerWorld);
        const footsLocal = computeArcPerpendicularFootsLocal(
            fromLocal,
            centerLocal,
            definition.radius,
            definition.startAngle,
            definition.endAngle,
            definition.clockwise,
        );
        return footsLocal.map((footLocal) => referencePlane.localToWorld(footLocal.x, footLocal.y));
    }

    /**
     * 计算椭圆垂足。
     *
     * @param firstPoint 绘制起点。
     * @param target 椭圆源对象。
     * @returns 垂足世界坐标列表。
     */
    private computeEllipseFootPointsWorld(firstPoint: THREE.Vector3, target: SketchEllipseObject): THREE.Vector3[] {
        const referencePlane = target.referencePlane;
        const fromLocal = referencePlane.worldToLocal(firstPoint);
        const definition = target.getEllipseDefinition();
        const centerLocal = referencePlane.worldToLocal(definition.centerWorld);
        const footsLocal = computeEllipsePerpendicularFootsLocal(
            fromLocal,
            centerLocal,
            definition.radiusX,
            definition.radiusY,
            definition.rotation,
        );
        return footsLocal.map((footLocal) => referencePlane.localToWorld(footLocal.x, footLocal.y));
    }
}


